import React from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { useTranslation } from '../../../utils/translation';
import { useImageComponent, useLinkComponent } from '../../../utils/component';
import { Cta } from '../../cta';
import { DatasheetAttribute } from './types';
import type { LinkProps } from '../../../atoms/types';
import { MarkStars } from '../../mark-with-stars';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { StarsMark } from '../../stars-with-mark';
import { MainCtaInfos } from '../../types';
import { MaterialSymbol } from '../../../atoms/material-symbol';

export type OnlineServiceProps = {
  size?: number;
  ctaLink?: LinkProps;
  productLink?: LinkProps;
  image: { imageId: string; alt: string };
  topDataSheet?: DatasheetAttribute[];
  title?: string;
  rating?: number;
  stickerName?: string;
  rank?: string;
  mainCtaInfos?: MainCtaInfos;
  customDisplays?: {
    hasRating?: boolean;
  };
};

const Wrapper = styled.div`
  background: var(--color-alt-4);
  box-shadow: 0px 0px 30px 0px var(--color-box-shadow);
  border-radius: 18px;
  display: flex;
  flex-flow: wrap;
  gap: 20px 15px;
  margin: 15px 0;
  padding: 60px 15px 15px;
  position: relative;
  top: 0;
  transition: all 0.3s;

  &:hover {
    text-decoration: none !important;
    top: -3px;
  }

  &.mod-top {
    border: solid 3px var(--theme-palette-blue);
  }

  &.mod-without-rank {
    padding: 30px 15px 15px;
  }

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      align-items: center;
      flex-flow: initial;
      gap: 30px;
      margin: var(--spacer-s) 0;
      padding: 60px 60px 30px;

      &.mod-without-rank {
        padding: 30px 60px;
      }
    `,
  )}
`;

const CtaWrapper = styled.div`
  bottom: 0;
  display: flex;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;

  > a {
    width: 100%;
  }
`;

const CardTitle = styled.div`
  font-family: var(--theme-typography-primary-font);
  font-weight: bold;
  line-height: 1.38;
  margin: 0;
  z-index: 1;

  &.mod-under {
    z-index: 0;
  }

  a {
    color: var(--title-foreground);
    text-decoration: none;
    transition: color 0.3s;
  }

  &:hover a {
    color: var(--theme-palette-red);
  }

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      margin: 0 0 15px;
    `,
  )}
`;

const CardTitleDesktop = styled(CardTitle)`
  display: none;
  font-size: 26px;

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      display: block;
    `,
  )}
`;

const CardTitleMobile = styled(CardTitle)`
  display: block;
  font-size: 22px;
  order: 1;
  width: 100%;

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      display: none;
    `,
  )}
`;

const Specs = styled.ul`
  list-style-type: none;
`;

const Spec = styled.li`
  font-size: 15px;
  line-height: 18px;
  margin: 0 0 8px;
  padding-left: 25px;
  position: relative;
`;

const SpecIcon = styled.span`
  font-size: 18px;
  left: 0;
  margin: 0 12px 0 0;
  position: absolute;
  top: -1px;
`;

const CardSpecs = styled.div`
  color: var(--theme-color-title);
  display: flex;
  flex-direction: column;
  font-size: 15px;
  list-style-type: none !important;
  margin: 0 0 12px;
  order: 4;
  width: 100%;

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      order: initial;
      width: initial;
    `,
  )}
`;

const CardRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-left: auto;
  order: 3;
  width: initial;

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      gap: 30px;
      min-width: 144px;
      order: initial;
    `,
  )}
`;

const CardRightDesktop = styled.div`
  display: none;

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      display: block;
    `,
  )}
`;

const CardRightMobile = styled.div`
  display: block;

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      display: none;
    `,
  )}
`;

const CardImage = styled.div`
  display: -ms-flexbox;
  display: flex;
  order: 2;

  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      order: initial;
    `,
  )}
`;

const CardSticker = styled.div`
  align-items: center;
  background: var(--theme-palette-blue-medium);
  border-bottom-right-radius: 17px;
  border-top-left-radius: 16px;
  color: white;
  display: flex;
  font-size: 18px;
  font-weight: 600;
  height: 30px;
  justify-content: center;
  left: -1px;
  padding: 0 15px;
  position: absolute;
  top: -1px;

  .mod-top & {
    background: var(--theme-palette-blue);
  }
`;

const getDefaultLabel = (
  layout: string,
  sponsored: boolean,
  internalLink: boolean,
  defaultLabels: { [key: string]: string },
): string => {
  const correspondingKey = Object.keys(defaultLabels).find((objectKey) => {
    return objectKey === layout;
  });

  if (correspondingKey) {
    if (!internalLink && sponsored) {
      return defaultLabels.external;
    }

    return defaultLabels[correspondingKey];
  }

  return defaultLabels.default;
};

const OnlineServiceCard = ({
  mainCtaInfos,
  image,
  title,
  rating,
  stickerName,
  topDataSheet,
  customDisplays,
  rank,
}: OnlineServiceProps) => {
  const t = useTranslation();
  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  const imageProps = {
    ...image,
    mobileWidth: 90,
    mobileHeight: 90,
    desktopWidth: 180,
    desktopHeight: 180,
  };

  const defaultLabels = {
    default: t('design.onlineServiceCard.defaultLabels.default'),
    external: t('design.onlineServiceCard.defaultLabels.external'),
  };

  const defaultLabel = getDefaultLabel(
    mainCtaInfos.layout,
    mainCtaInfos.sponsored,
    mainCtaInfos.internalLink,
    defaultLabels,
  );

  let stickerInt = '';
  if (rank !== undefined) {
    stickerInt = `${rank}. `;
  }

  if (stickerName) {
    stickerInt += `${stickerName}`;
  }

  return (
    <Wrapper
      className={classNames({
        'mod-top': stickerInt?.includes('1.'),
        'mod-without-rank': rank === undefined,
      })}
    >
      <CtaWrapper>
        <LinkComponent {...mainCtaInfos.ctaPropsCard}>{title}</LinkComponent>
      </CtaWrapper>
      {stickerInt !== '' && <CardSticker>{stickerInt}</CardSticker>}
      <CardTitleMobile className={mainCtaInfos?.layout === 'download' ? 'mod-under' : ''}>{title}</CardTitleMobile>
      <CardImage>
        <ImageComponent {...imageProps} />
      </CardImage>
      <CardSpecs>
        <CardTitleDesktop className={mainCtaInfos?.layout === 'download' ? 'mod-under' : ''}>{title}</CardTitleDesktop>
        <Specs>
          {topDataSheet?.map((spec: DatasheetAttribute, index: number) => (
            <Spec key={index}>
              <MaterialSymbol
                className="material-symbols-outlined"
                symbol={spec.iconSlug}
                Component={SpecIcon}
                lazy={false}
              />
              {spec.type === 'numeric' ? `${spec.value} ${spec.name.toLowerCase()}` : spec.value}
            </Spec>
          ))}
        </Specs>
      </CardSpecs>
      <CardRight>
        <CardRightDesktop>
          {rating && customDisplays.hasRating ? (
            <div>
              <MarkStars className="mod-dark" rating={rating} max={10} />
            </div>
          ) : null}
        </CardRightDesktop>
        <CardRightMobile>
          {rating && customDisplays.hasRating ? (
            <div>
              <StarsMark rating={rating / 2} max={5} ratingLabel={rating} maxLabel={10} />
            </div>
          ) : null}
        </CardRightMobile>
        {mainCtaInfos.link && <Cta {...mainCtaInfos.ctaPropsCard}>{mainCtaInfos?.label || defaultLabel}</Cta>}
      </CardRight>
    </Wrapper>
  );
};

export default OnlineServiceCard;
